import { useImperativeHandle, useMemo } from 'react';
import { shallowEqual, useSelector as useReduxSelector } from 'react-redux';
import { type AnyFieldApi, useForm, useSelector } from '@tanstack/react-form';

import { useAuth } from '@nvision/identity-core';
import { DatePickerField, InputField } from '@nvision/ui';

import {
  CargoTypeField,
  CarrierField,
  PricingCategoryField,
  PricingTypeField,
  SupplierDiversityField,
  TariffTypeField,
} from '@/modules/entities';
import { FormSchema, useFormSchema } from '@/modules/validators';
import type { RootState } from '@/redux/RootReducer';
import { getEffectiveDateBounds } from '@/utils/date';

import type { NewContract } from '../../../../models';

import styles from '../contract-details.module.css';

export type ContractFormRef = {
  canSubmit: () => boolean;
  isDirty: () => boolean;
  reset: (values: NewContract) => void;
  submit: () => void;
};

export interface ContractFormProps {
  formRef: React.RefObject<ContractFormRef>;
  initialValues: NewContract;
  onSubmit: (values: NewContract) => void;
}

export const ContractForm: React.FC<ContractFormProps> = ({ formRef, initialValues, onSubmit }) => {
  const selectedCompany = useReduxSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const customerKey = selectedCompany?.companyId ?? 0;

  const { valibotSchema } = useFormSchema();

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      if (value) {
        onSubmit(value);
      }
    },
    validators: { onChange: valibotSchema },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    isDirty: () => !form.state.isDefaultValue,
    reset: (values: NewContract) => {
      form.reset(values, { keepDefaultValues: false });
    },
    submit: () => {
      form.handleSubmit();
    },
  }));

  const values = useSelector(form.store, (state) => state.values);
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const { effectiveFromMaxValue, effectiveToMinValue } = useMemo(
    () => getEffectiveDateBounds(values.effectiveFrom, values.effectiveTo, timeZone),
    [timeZone, values.effectiveFrom, values.effectiveTo],
  );

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: { message?: string }) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <form className={styles.grid} onSubmit={handleSubmit}>
      <FormSchema.Field name="name">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <InputField
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                label={label}
                placeholder="Enter"
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="uniqueName">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <InputField
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                label={label}
                placeholder="Enter"
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="carrier">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <CarrierField
                value={field.state.value ?? null}
                clearable
                defaultFilters={{ customerKey }}
                onSelectionChange={field.handleChange}
                label={label}
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="effectiveFrom">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <DatePickerField
                value={field.state.value}
                maxValue={effectiveFromMaxValue}
                onChange={(date) => field.handleChange(date ?? undefined)}
                label={label}
                size="sm"
                timeZone={timeZone}
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="effectiveTo">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <DatePickerField
                value={field.state.value}
                minValue={effectiveToMinValue}
                includeEndOfDay
                onChange={(date) => field.handleChange(date ?? undefined)}
                label={label}
                size="sm"
                timeZone={timeZone}
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="cargoType">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <CargoTypeField
                value={field.state.value ?? null}
                clearable
                onSelectionChange={field.handleChange}
                label={label}
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="pricingBasis">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <TariffTypeField
                value={field.state.value ?? null}
                clearable
                onSelectionChange={field.handleChange}
                label={label}
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="pricingType">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <PricingTypeField
                value={field.state.value ?? null}
                clearable
                onSelectionChange={field.handleChange}
                label={label}
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="pricingCategory">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <PricingCategoryField
                value={field.state.value ?? null}
                clearable
                onSelectionChange={field.handleChange}
                label={label}
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <FormSchema.Field name="supplierDiversity">
        {({ isRequired, label, name }) => (
          <form.Field name={name}>
            {(field) => (
              <SupplierDiversityField
                value={field.state.value ?? null}
                clearable
                onSelectionChange={field.handleChange}
                label={label}
                size="sm"
                isRequired={isRequired}
                errorMessage={getError(field)}
                className={styles.field}
              />
            )}
          </form.Field>
        )}
      </FormSchema.Field>

      <button type="submit" style={{ display: 'none' }}>
        Hidden Submit
      </button>
    </form>
  );
};
